import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 15000;
const DEVELOPER_EMAIL = "patilyasshh@gmail.com";

const MaintenanceContext = createContext({
  enabled: false,
  message: "",
  resume_at: null,
  allowed_users: [],
  isAllowed: true,
  loading: true,
  refresh: async () => {},
});

export const useMaintenance = () => useContext(MaintenanceContext);

export const MaintenanceProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [state, setState] = useState({
    enabled: false,
    message: "",
    resume_at: null,
    allowed_users: [],
    isAllowed: true,
    loading: true,
  });
  const intervalRef = useRef(null);

  const computeIsAllowed = useCallback((data, currentUser) => {
    if (!data?.enabled) return true;
    if (typeof data.is_allowed === "boolean") return data.is_allowed;
    const email = (currentUser?.email || "").toLowerCase();
    const role = (currentUser?.user_metadata?.role || currentUser?.role || "").toLowerCase();
    if (!email) return false;
    if (email === DEVELOPER_EMAIL) return true;
    if (["owner", "developer", "admin"].includes(role)) return true;
    const list = (data.allowed_users || []).map((u) => String(u).toLowerCase());
    return list.includes(email);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API}/maintenance`, { headers });
      const data = await res.json();
      setState({
        enabled: !!data.enabled,
        message: data.message || "",
        resume_at: data.resume_at || null,
        allowed_users: data.allowed_users || [],
        isAllowed: computeIsAllowed(data, user),
        loading: false,
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [token, user, computeIsAllowed]);

  useEffect(() => {
    refresh();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refresh]);

  return (
    <MaintenanceContext.Provider value={{ ...state, refresh }}>
      {children}
    </MaintenanceContext.Provider>
  );
};
